import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, TrendingUp, Users, Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_LABELS = {
  classic: "X O X",
  gomoku: "Gomoku",
  sos: "SOS",
  memory: "Memory Card",
  maze: "Labirin Ajaib",
  whack: "Pukul Jawaban!",
  snake: "Ular Kata Ceria",
  block: "Balok Bentuk Pintar",
  simon: "Ikuti Lampu Ajaib",
};

export const Leaderboard = ({ isOpen, onClose }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leaderboard");

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, statsRes] = await Promise.all([
        axios.get(`${API}/game/leaderboard`),
        axios.get(`${API}/game/stats`),
      ]);
      setLeaderboard(leaderboardRes.data);
      setStats(statsRes.data);
    } catch (error) {
      logger.error("Failed to fetch leaderboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (index) => {
    if (index === 0) return "text-yellow-300";
    if (index === 1) return "text-gray-300";
    if (index === 2) return "text-orange-300";
    return "text-gold-light/80";
  };

  const getRankIcon = (index) => {
    if (index < 3) return <Trophy className={`w-5 h-5 ${getRankColor(index)}`} />;
    return <span className="w-5 h-5 flex items-center justify-center font-fredoka text-sm text-gold-light/80">{index + 1}</span>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card-strong border-gold/20 max-w-lg max-h-[80vh] overflow-hidden rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-fredoka text-2xl text-white flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-300" />
            Papan Skor
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Button data-testid="leaderboard-button"
            variant={activeTab === "leaderboard" ? "default" : "outline"}
            onClick={() => setActiveTab("leaderboard")}
            className={`flex-1 font-fredoka font-bold rounded-xl ${
              activeTab === "leaderboard"
                ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-0"
                : "glass-card border-gold/20 text-gold hover:bg-gold/10"
            }`}
          >
            <Users className="w-4 h-4 mr-2" />Top Pemain
          </Button>
          <Button data-testid="global-stats-button"
            variant={activeTab === "stats" ? "default" : "outline"}
            onClick={() => setActiveTab("stats")}
            className={`flex-1 font-fredoka font-bold rounded-xl ${
              activeTab === "stats"
                ? "bg-gradient-to-r from-purple-400 to-indigo-500 text-white border-0"
                : "glass-card border-gold/20 text-gold hover:bg-gold/10"
            }`}
          >
            <TrendingUp className="w-4 h-4 mr-2" />Statistik
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {activeTab === "leaderboard" ? (
                <motion.div key="leaderboard" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-2">
                  {leaderboard.length === 0 ? (
                    <div className="text-center py-8">
                      <Gamepad2 className="w-12 h-12 mx-auto mb-4 text-gold-light/40" />
                      <p className="font-fredoka text-gold-light/80">Belum ada permainan.</p>
                      <p className="font-fredoka text-gold-light/50 text-sm">Main dulu yuk!</p>
                    </div>
                  ) : (
                    leaderboard.map((entry, index) => (
                      <motion.div key={entry.player_name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                        className="flex items-center gap-3 glass-card rounded-xl p-3"
                      >
                        <div className="w-8 flex justify-center">{getRankIcon(index)}</div>
                        <div className="flex-1">
                          <p className={`font-fredoka font-bold ${getRankColor(index)}`}>{entry.player_name}</p>
                          <p className="font-fredoka text-xs text-gold-light/50">{MODE_LABELS[entry.best_mode] || entry.best_mode}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-fredoka font-bold text-yellow-300">{entry.total_score > 0 ? `${entry.total_score} pts` : `${entry.wins} wins`}</p>
                          <p className="font-fredoka text-xs text-gold-light/50">{entry.win_rate}% ({entry.games_played} game)</p>
                        </div>
                      </motion.div>
                    ))
                  )}
                </motion.div>
              ) : (
                <motion.div key="stats" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                  {stats && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="glass-card rounded-xl p-4 text-center">
                          <p className="font-fredoka text-2xl text-yellow-300 font-bold">{stats.total_games}</p>
                          <p className="font-fredoka text-sm text-gold-light/80">Total Game</p>
                        </div>
                        <div className="glass-card rounded-xl p-4 text-center">
                          <p className="font-fredoka text-2xl text-cyan-300 font-bold">{stats.total_players}</p>
                          <p className="font-fredoka text-sm text-gold-light/80">Pemain</p>
                        </div>
                      </div>
                      <div className="glass-card rounded-xl p-4">
                        <p className="font-fredoka text-gold-light mb-3 font-bold">Distribusi Menang</p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-fredoka text-yellow-300">X Menang</span>
                          <span className="font-fredoka font-bold text-yellow-300">{stats.x_wins}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-fredoka text-orange-300">O Menang</span>
                          <span className="font-fredoka font-bold text-orange-300">{stats.o_wins}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-fredoka text-gold-light/60">Seri</span>
                          <span className="font-fredoka font-bold text-gold-light/60">{stats.draws}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="glass-card rounded-xl p-4 text-center">
                          <p className="font-fredoka text-lg text-pink-300 font-bold capitalize">{MODE_LABELS[stats.most_popular_mode] || stats.most_popular_mode}</p>
                          <p className="font-fredoka text-sm text-gold-light/80">Mode Favorit</p>
                        </div>
                        <div className="glass-card rounded-xl p-4 text-center">
                          <p className="font-fredoka text-lg text-green-300 font-bold">{stats.total_score || 0}</p>
                          <p className="font-fredoka text-sm text-gold-light/80">Total Skor</p>
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Leaderboard;
